import {
  deleteCPMAgreement,
  getTFAProgramSuccess,
  uploadCPMAgreement,
} from '@/services/cpm/cpmProgramManagement.service'
import type { IProgramSuccessFilters } from '@/types/cpm/programSuccess'
import { NotifyErrorWrapper, NotifySuccess } from '@/helpers/core/notifications'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import type { ComputedRef } from 'vue'
import { computed } from 'vue'

const PROGRAM_SUCCESS_QUERY_KEY = ['cpm', 'tfa-program-success']

export function useProgramSuccess() {
  const queryClient = useQueryClient()

  const getProgramSuccessQuery = (filters: ComputedRef<IProgramSuccessFilters>) =>
    useQuery({
      queryKey: [...PROGRAM_SUCCESS_QUERY_KEY, filters],
      queryFn: async () => getTFAProgramSuccess(filters.value),
      enabled: computed(() => !!filters.value.site_id),
      // Keep the currently shown data while a new interval/site fetches, so the
      // chart animates from the old dataset instead of flashing empty first.
      placeholderData: keepPreviousData,
    })

  // The current agreement is surfaced on the program-success payload, so both
  // mutations refresh that query rather than maintaining separate state.
  const uploadAgreementMutation = useMutation({
    mutationFn: ({ siteId, document, name }: { siteId: string; document: File; name?: string }) =>
      uploadCPMAgreement(siteId, document, name),
    onSuccess: () => {
      NotifySuccess('Agreement uploaded successfully.')
      queryClient.invalidateQueries({ queryKey: PROGRAM_SUCCESS_QUERY_KEY })
    },
    onError: NotifyErrorWrapper('Failed to upload agreement'),
  })

  const deleteAgreementMutation = useMutation({
    mutationFn: (siteId: string) => deleteCPMAgreement(siteId),
    onSuccess: () => {
      NotifySuccess('Agreement removed successfully.')
      queryClient.invalidateQueries({ queryKey: PROGRAM_SUCCESS_QUERY_KEY })
    },
    onError: NotifyErrorWrapper('Failed to remove agreement'),
  })

  return { getProgramSuccessQuery, uploadAgreementMutation, deleteAgreementMutation }
}
